import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStoreCart } from '../context/StoreCartContext';
import { initTheme, toggleTheme } from '../utils/theme';
import api from '../utils/api';
import LoginModal from './LoginModal';
import SignupModal from './SignupModal';
import NotificationBell from './NotificationBell';
import logoImage from '../asset/logo.jpeg';

const UserDropdown = ({ user, walletAddress, logout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
          {user.username?.[0]?.toUpperCase() || 'U'}
        </div>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:block">
          {user.username}
        </span>
        <svg
          className={`w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{user.username}</p>
            {user.email && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{user.email}</p>
            )}
            {walletAddress && (
              <p className="text-xs text-gray-500 dark:text-gray-500 font-mono mt-1">
                {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
              </p>
            )}
          </div>
          <div className="py-1">
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Profile
            </Link>
            <Link
              to="/wallet"
              onClick={() => setIsOpen(false)}
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2m2-4h-6a2 2 0 000 4h6a2 2 0 000-4z" />
              </svg>
              Wallet
            </Link>
            <Link
              to="/referral"
              onClick={() => setIsOpen(false)}
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Referral
            </Link>
            <Link
              to="/store"
              onClick={() => setIsOpen(false)}
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M5 6h14l1 4H4l1-4zm0 4v8h14v-8M9 18v-4h6v4" />
              </svg>
              Golden Store
            </Link>
            <Link
              to="/store/orders"
              onClick={() => setIsOpen(false)}
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Store orders
            </Link>
            {user.canAccessAdmin && (
              <Link
                to={user.role === 'superAdmin' ? '/superadmin' : '/admin'}
                onClick={() => setIsOpen(false)}
                className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {user.role === 'superAdmin' ? 'Super Admin' : 'Admin'}
              </Link>
            )}
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const [theme, setTheme] = useState('light');
  const [searchQuery, setSearchQuery] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [navbarCups, setNavbarCups] = useState([]);
  const [homeLandingEnabled, setHomeLandingEnabled] = useState(false);
  const [socialLinks, setSocialLinks] = useState({
    socialTwitter: '',
    socialFacebook: '',
    socialInstagram: '',
    socialYoutube: '',
    socialDiscord: '',
  });
  const { user, logout, walletAddress } = useAuth();
  const { itemCount: storeCartCount } = useStoreCart();
  const navigate = useNavigate();

  useEffect(() => {
    setTheme(initTheme());
    fetchNavbarCups();
    fetchSocialLinks();
    fetchLandingConfig();
  }, []);

  const fetchNavbarCups = async () => {
    try {
      const response = await api.get('/cups/navbar');
      setNavbarCups(response.data || []);
    } catch (error) {
      console.error('Error fetching navbar cups:', error);
      setNavbarCups([]);
    }
  };

  const fetchLandingConfig = async () => {
    try {
      const { data } = await api.get('/settings/landing');
      setHomeLandingEnabled(!!data?.homeLandingEnabled);
    } catch (error) {
      console.error('Error fetching landing config:', error);
    }
  };

  const fetchSocialLinks = async () => {
    try {
      const response = await api.get('/settings/social-links');
      if (response.data) {
        setSocialLinks({
          socialTwitter: response.data.socialTwitter || '',
          socialFacebook: response.data.socialFacebook || '',
          socialInstagram: response.data.socialInstagram || '',
          socialYoutube: response.data.socialYoutube || '',
          socialDiscord: response.data.socialDiscord || '',
        });
      } else {
        // Initialize with empty strings if no data
        setSocialLinks({
          socialTwitter: '',
          socialFacebook: '',
          socialInstagram: '',
          socialYoutube: '',
          socialDiscord: '',
        });
      }
    } catch (error) {
      console.error('Error fetching social links:', error);
      // Initialize with empty strings on error so icons still show
      setSocialLinks({
        socialTwitter: '',
        socialFacebook: '',
        socialInstagram: '',
        socialYoutube: '',
        socialDiscord: '',
      });
    }
  };

  // Helper function to format link - adds https:// if missing
  const formatLink = (link) => {
    if (!link) return null;
    const trimmed = link.trim();
    if (!trimmed) return null;
    // If it doesn't start with http:// or https://, add https://
    if (!trimmed.match(/^https?:\/\//i)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  const handleThemeToggle = () => {
    const newTheme = toggleTheme();
    setTheme(newTheme);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <>
      <nav className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
        {/* First Row */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Name */}
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src={logoImage} 
                alt="WE R GAME"
                className="h-10 w-auto object-contain"
              />
              <span className="text-xl font-bold text-gray-900 dark:text-white hidden sm:block">WE R GAME</span>
            </Link>

            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 max-w-lg mx-8">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search matches, teams..."
                  className="w-full px-4 py-2 pl-10 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                />
                <svg
                  className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </form>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {/* Theme Toggle */}
              <button
                onClick={handleThemeToggle}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <svg className="w-6 h-6 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {user && <NotificationBell />}

              {/* Social Media Icons */}
              <div className="hidden md:flex items-center space-x-2">
                {/* X (Twitter) */}
                {formatLink(socialLinks.socialTwitter) ? (
                        <a 
                          href={formatLink(socialLinks.socialTwitter)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors cursor-pointer"
                          title="X (Twitter)"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                        </a>
                      ) : (
                        <span 
                          className="text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50"
                          title="X (Twitter) - Link not set"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                        </span>
                      )}
                      
                      {/* Facebook */}
                      {formatLink(socialLinks.socialFacebook) ? (
                        <a 
                          href={formatLink(socialLinks.socialFacebook)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors cursor-pointer"
                          title="Facebook"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                          </svg>
                        </a>
                      ) : (
                        <span 
                          className="text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50"
                          title="Facebook - Link not set"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                          </svg>
                        </span>
                      )}
                      
                      {/* Instagram */}
                      {formatLink(socialLinks.socialInstagram) ? (
                        <a 
                          href={formatLink(socialLinks.socialInstagram)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors cursor-pointer"
                          title="Instagram"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                          </svg>
                        </a>
                      ) : (
                        <span 
                          className="text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50"
                          title="Instagram - Link not set"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                          </svg>
                        </span>
                      )}
                      
                      {/* YouTube */}
                      {formatLink(socialLinks.socialYoutube) ? (
                        <a 
                          href={formatLink(socialLinks.socialYoutube)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors cursor-pointer"
                          title="YouTube"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                          </svg>
                        </a>
                      ) : (
                        <span 
                          className="text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50"
                          title="YouTube - Link not set"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                          </svg>
                        </span>
                      )}

                      {/* Discord */}
                      {formatLink(socialLinks.socialDiscord) ? (
                        <a
                          href={formatLink(socialLinks.socialDiscord)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors cursor-pointer"
                          title="Discord"
                          aria-label="Join us on Discord"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M20.317 4.3698A19.7913 19.7913 0 0015.8854 3c-.1911.3443-.4031.8014-.5533 1.1662a18.27 18.27 0 00-5.6643 0A12.64 12.64 0 009.1044 3a19.736 19.736 0 00-4.4327 1.3713C1.8698 8.5447 1.1118 12.6157 1.4904 16.6297a19.9 19.9 0 005.4383 2.7433 13.4 13.4 0 001.1665-1.9 12.9 12.9 0 01-1.8347-.8797c.154-.112.304-.2287.4498-.3498 3.537 1.634 7.3707 1.634 10.8652 0 .147.121.297.238.4499.3498-.5834.339-1.1983.633-1.835.8797a13.3 13.3 0 001.1664 1.9 19.85 19.85 0 005.439-2.7433c.4447-4.6512-.7593-8.6851-2.4787-12.2599zM8.0207 14.1727c-1.0602 0-1.9294-.9775-1.9294-2.178 0-1.2006.8502-2.178 1.9294-2.178 1.089 0 1.9482.9866 1.9294 2.178 0 1.2005-.8502 2.178-1.9294 2.178zm7.9748 0c-1.0602 0-1.9294-.9775-1.9294-2.178 0-1.2006.8502-2.178 1.9294-2.178 1.089 0 1.9482.9866 1.9294 2.178 0 1.2005-.8407 2.178-1.9294 2.178z" />
                          </svg>
                        </a>
                      ) : (
                        <span
                          className="text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50"
                          title="Discord - Link not set"
                          aria-label="Discord link not set"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M20.317 4.3698A19.7913 19.7913 0 0015.8854 3c-.1911.3443-.4031.8014-.5533 1.1662a18.27 18.27 0 00-5.6643 0A12.64 12.64 0 009.1044 3a19.736 19.736 0 00-4.4327 1.3713C1.8698 8.5447 1.1118 12.6157 1.4904 16.6297a19.9 19.9 0 005.4383 2.7433 13.4 13.4 0 001.1665-1.9 12.9 12.9 0 01-1.8347-.8797c.154-.112.304-.2287.4498-.3498 3.537 1.634 7.3707 1.634 10.8652 0 .147.121.297.238.4499.3498-.5834.339-1.1983.633-1.835.8797a13.3 13.3 0 001.1664 1.9 19.85 19.85 0 005.439-2.7433c.4447-4.6512-.7593-8.6851-2.4787-12.2599zM8.0207 14.1727c-1.0602 0-1.9294-.9775-1.9294-2.178 0-1.2006.8502-2.178 1.9294-2.178 1.089 0 1.9482.9866 1.9294 2.178 0 1.2005-.8502 2.178-1.9294 2.178zm7.9748 0c-1.0602 0-1.9294-.9775-1.9294-2.178 0-1.2006.8502-2.178 1.9294-2.178 1.089 0 1.9482.9866 1.9294 2.178 0 1.2005-.8407 2.178-1.9294 2.178z" />
                          </svg>
                        </span>
                      )}
              </div>

              {/* Auth Buttons */}
              {user ? (
                <UserDropdown user={user} walletAddress={walletAddress} logout={logout} />
              ) : (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowLogin(true)}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-blue-500 transition-colors"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => setShowSignup(true)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Second Row - Cups and quick links */}
        <div className="relative border-t border-gray-200 dark:border-gray-700">
          <div
            className="max-w-7xl mx-auto flex h-11 items-center overflow-x-auto overscroll-x-contain scroll-smooth px-3 sm:px-6 lg:px-8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            aria-label="Tournament and quick navigation"
          >
            <div className="flex shrink-0 items-center gap-0.5">
              {homeLandingEnabled ? (
                <Link
                  to="/"
                  className="whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                >
                  Home
                </Link>
              ) : null}
              {navbarCups.map((cup) => (
                <Link
                  key={cup._id}
                  to={`/cup/${cup.slug}`}
                  className="whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                >
                  {cup.name}
                </Link>
              ))}
            </div>

            <span
              className="ml-auto mr-2.5 h-6 w-px shrink-0 bg-red-500/80 shadow-[0_0_6px_rgba(239,68,68,0.25)] sm:mr-3.5"
              aria-hidden="true"
            />

            <div className="flex shrink-0 items-center gap-0.5">
              <Link
                to="/leaderboard"
                className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                title="Leaderboard"
              >
                <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                <span className="hidden lg:inline">Leaderboard</span>
              </Link>
              <Link
                to="/streaks"
                className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                title="Streaks"
              >
                <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="hidden lg:inline">Streaks</span>
              </Link>
              <Link
                to="/jackpot"
                className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                title="Jackpots"
              >
                <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="hidden lg:inline">Jackpots</span>
              </Link>
              <Link
                to="/referral"
                className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                title="Referral"
              >
                <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                <span className="hidden lg:inline">Referral</span>
              </Link>
              <Link
                to="/store"
                className="relative flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/20 transition-colors"
                title="Golden Store"
              >
                <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M5 6h14l1 4H4l1-4zm0 4v8h14v-8M9 18v-4h6v4" />
                </svg>
                <span className="hidden lg:inline">Store</span>
                {storeCartCount > 0 && (
                  <span className="absolute -right-1 -top-1 min-w-[16px] rounded-full bg-amber-400 px-1 text-center text-[9px] font-black leading-4 text-slate-950 ring-2 ring-white dark:ring-gray-900">
                    {storeCartCount > 99 ? '99+' : storeCartCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} onSwitchToSignup={() => { setShowLogin(false); setShowSignup(true); }} />}
      {showSignup && <SignupModal onClose={() => setShowSignup(false)} onSwitchToLogin={() => { setShowSignup(false); setShowLogin(true); }} />}
    </>
  );
};

export default Navbar;
